import type { RegionKey, USState } from '../types';

/**
 * Region metadata registry.
 *
 * Single source of truth for the travel-region titles, copy, palette hints,
 * and "best-for" summaries surfaced on state hubs and region hubs.
 * `lib/trailHubs.ts` composes this with the raw `CHEESERIES` /
 * `ACCOMMODATIONS` arrays so region membership is always derived from the
 * data itself (each row carries a `region` key) rather than hardcoded
 * duplicate lists.
 *
 * Accent palette strings follow the brand-evolution plan: each region gets
 * a subtle regional accent applied on top of the shared design system.
 * They are descriptive only — mapping to Tailwind tokens happens in the UI.
 */
export interface RegionMeta {
  key: RegionKey;
  state: USState;
  /** Full editorial title, used as the region-hub H1. */
  title: string;
  /** Short label for chips, breadcrumbs, and footer references. */
  shortTitle: string;
  /** One-sentence editorial subtitle, used under the title. */
  subtitle: string;
  /** Atmosphere line that sets the tone for the region's card. */
  mood: string;
  /** Human-readable accent description (ties into per-region palettes later). */
  accent: string;
  /** 3–5 short phrases describing who/what this region suits. */
  bestFor: string[];
}

export const REGIONS: RegionMeta[] = [
  // ------------------------------------------------------------------
  // VERMONT
  // ------------------------------------------------------------------
  {
    key: 'champlain-valley',
    state: 'VT',
    title: 'Champlain Valley & Burlington',
    shortTitle: 'Champlain Valley',
    subtitle:
      'Lake Champlain estates, Burlington food culture, and goat-cheese stops tucked into the broad western valley.',
    mood: 'Lake views, college-town restaurants, and easy farm-store detours.',
    accent: 'Lake blue, pasture green, cream',
    bestFor: ['Lakefront stays', 'Walkable city food', 'Goat cheese', 'Short drives between stops'],
  },
  {
    key: 'mad-river-green-mountains',
    state: 'VT',
    title: 'Mad River Valley & Green Mountains',
    shortTitle: 'Green Mountains',
    subtitle:
      'Route 100 dairy country, maple farms, Alpine-style wheels, and polished country inns between Waterbury and Woodstock.',
    mood: 'Mountain drives, maple shelves, and long-weekend inn culture.',
    accent: 'Moss, cream, barn red',
    bestFor: ['Route 100 weekends', 'Country inns', 'Maple pairings', 'Alpine-style cheese'],
  },
  {
    key: 'northeast-kingdom',
    state: 'VT',
    title: 'Northeast Kingdom',
    shortTitle: 'Northeast Kingdom',
    subtitle:
      'Remote hill towns and cave-aged icons, with Greensboro as the spiritual center for Vermont natural-rind cheese.',
    mood: 'Quiet roads, village stores, and serious cheese reputation.',
    accent: 'Spruce, granite, oxblood',
    bestFor: ['Cave-aged icons', 'Remote drives', 'Cheese pilgrimage', 'Fall foliage'],
  },
  {
    key: 'southern-vermont',
    state: 'VT',
    title: 'Southern Vermont & Connecticut River Valley',
    shortTitle: 'Southern Vermont',
    subtitle:
      'Historic factories, sheep-milk wheels, Brattleboro provisions, and old New England villages near the river line.',
    mood: 'Covered-bridge drives, antique towns, and deep cheese history.',
    accent: 'Brick red, old parchment, pine',
    bestFor: ['Historic cheesemaking', 'Antique villages', 'Sheep cheese', 'River-valley drives'],
  },

  // ------------------------------------------------------------------
  // MAINE
  // ------------------------------------------------------------------
  {
    key: 'greater-portland-casco-bay',
    state: 'ME',
    title: 'Greater Portland & Casco Bay',
    shortTitle: 'Portland & Casco Bay',
    subtitle:
      'Easy Portland day trips, working dairy farms, farm markets, and coastal stays within reach of the Old Port.',
    mood: 'Harbor restaurants, family-friendly farms, and short drives.',
    accent: 'Sea blue, weathered wood, butter yellow',
    bestFor: ['Portland weekends', 'Family-friendly farms', 'Harbor dining', 'Day-trip radius'],
  },
  {
    key: 'midcoast-maine',
    state: 'ME',
    title: 'Midcoast Maine',
    shortTitle: 'Midcoast Maine',
    subtitle:
      'Appleton, Rockland, Camden, and central-Maine farm shops linked by markets, harbors, and inland pasture roads.',
    mood: 'Farmers markets, harbor towns, and slower coastal weekends.',
    accent: 'Fog gray, rope tan, harbor blue',
    bestFor: ['Farmers markets', 'Harbor towns', 'Slow weekends', 'Coastal + inland mix'],
  },
  {
    key: 'acadia-mount-desert',
    state: 'ME',
    title: 'Acadia & Mount Desert Island',
    shortTitle: 'Acadia',
    subtitle:
      'A compact goat-cheese detour at the edge of Acadia, best treated as a park-and-cheese weekend rather than a dense trail.',
    mood: 'Park loops, garden walks, and one very worthwhile farmstand stop.',
    accent: 'Granite, pine, ocean',
    bestFor: ['Acadia park days', 'Goat cheese detour', 'Garden walks', 'Single-stop weekends'],
  },

  // ------------------------------------------------------------------
  // NEW HAMPSHIRE (draft — all member stops are currently verified:false
  // and do not render on the public site. Verify entries before publishing.)
  // ------------------------------------------------------------------
  {
    key: 'white-mountains-nh',
    state: 'NH',
    title: 'White Mountains & Northern New Hampshire',
    shortTitle: 'White Mountains',
    subtitle:
      'Notch roads, historic country stores, and small goat and cow dairies scattered through the Sandwich Range and the upper Connecticut River Valley.',
    mood: 'Mountain passes, old retail icons, and quiet farm stops.',
    accent: 'Granite, birch, moss',
    bestFor: ['Mountain weekends', 'Historic country stores', 'Quiet farmstand stops', 'Fall foliage'],
  },
  {
    key: 'merrimack-valley-nh',
    state: 'NH',
    title: 'Merrimack Valley & Central New Hampshire',
    shortTitle: 'Merrimack Valley',
    subtitle:
      'The state\u2019s agricultural spine between Concord and the Lakes Region \u2014 Shaker village dairy heritage, biodynamic farms, and family creameries within an hour of the capital.',
    mood: 'Rolling farm country, Shaker villages, and capital-region weekends.',
    accent: 'Hay gold, field green, river blue',
    bestFor: ['Concord day trips', 'Biodynamic farms', 'Shaker heritage', 'Family creameries'],
  },
];

export function getRegion(key: RegionKey): RegionMeta | undefined {
  return REGIONS.find((r) => r.key === key);
}

export function getRegionsByState(state: USState): RegionMeta[] {
  return REGIONS.filter((r) => r.state === state);
}
